import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";

import { apiConfig } from 'config/apiConfig'

import useFetchWithMsal from 'hooks/useFetchWithMsal';

import { FormControlLabel, Grid, Radio, RadioGroup, Tooltip, tooltipClasses } from '@mui/material';
import { Formik, Form, ErrorMessage } from 'formik';
import FormField from 'components/Elements/Forms/FormField';
import SoftAvatar from 'components/SoftAvatar';
import FormSwitch from 'components/Elements/Forms/FormSwitch';
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import { styled } from '@mui/material/styles';


import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";

import { ProgressIndicator, useProgressIndicator } from 'components/Elements/ProgressIndicator';

import { useCardroomContext } from 'features';


const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 500,
    },
});


const RebateScheduleEditorForm = ({ cardroom, agency, club, book, schedule, formData, dense }) => {

    const { formField, values, errors, touched, setFieldValue, setFieldError, setFieldTouched, validateForm, resetForm } = formData;
    const { scheduleId, name, description, rebateRate, rebateThreshold, rebateScheduleScheme, lossOnly, makeupThreshold } = formField;
    const {
        scheduleId: scheduleIdV,
        name: nameV,
        description: descriptionV,
        rebateRate: rebateRateV,
        rebateThreshold: rebateThresholdV,
        makeupThreshold: makeupThresholdV,
        rebateScheduleScheme: rebateScheduleSchemeV,
        lossOnly: lossOnlyV,
        //chipOffsetRate: chipOffsetRateV, 
        //taxRebateRate: taxRebateRateV
    } = values;

    const [formDataInitialized, setFormDataInitialized] = useState(false);


    const [context, actions, features] = useCardroomContext(cardroom);

    function canEditSchedule() {
        if (!context) return false;


        return context.isAllowed(actions.cardroom.fees.rebate.edit);
    }

    function getFeeShemeDisplayText(v) {
        if (v === 0) return "none";
        if (v === 1) return "flat";
        if (v === 2) return "progressive";
        if (v === 3) return "step";
        return "none";
    }

    function getFeeSchemeDescription(v) {
        if (v === 0) return "no fees are imputed";
        if (v === 1) return "Fees are imputed as percentage of total earnings";
        if (v === 2) return "Fees are imputed as percentage of earnings above commission threshold.";
        if (v == 3) return "Fees are imputed based on player's controbution level"

        return "unknown";
    }

    function onRebateSchedChanged(e, newValue) {
        let scheme = parseInt(newValue);
        if (isNaN(scheme)) return;
        setFieldValue(rebateScheduleScheme.name, scheme);
    }

    useEffect(() => {
        if (!formDataInitialized) {

            setFieldValue(rebateScheduleScheme.name, schedule && schedule.id ? schedule.scheme : 0);
            setFieldValue(rebateRate.name, schedule && schedule.id ? schedule.rate * 100 : 0);
            setFieldValue(rebateThreshold.name, schedule && schedule.id ? schedule.threshold : 0);

            setFieldValue(name.name, schedule && schedule.id ? schedule.name : "");
            setFieldValue(description.name, schedule && schedule.id ? schedule.description : "");
            setFieldValue(scheduleId.name, schedule ? schedule.id : "");

            if (cardroom.type !== 6) {
                setFieldValue(lossOnly.name, schedule && schedule.id ? schedule.rebateOnLossOnly : false);
            } else {
                setFieldValue(makeupThreshold.name, schedule && schedule.id ? schedule.makeupThreshold : 0);
            }

            setFormDataInitialized(true);
        }
    }, [formDataInitialized]);

    useEffect(() => {
        if (schedule && formDataInitialized) {
            setFormDataInitialized(false);
        }
    }, [schedule])

    return <SoftBox>
        <SoftBox mt={2}>
            <Grid container spacing={1}>
                {dense === true ? null :
                    <Grid item xs={12} sm={4} container justifyContent="center">
                        <SoftBox position="relative" height="max-content" mx="auto">
                            <SoftAvatar src={null} size="xxl" variant="rounded" bgColor="dark" />
                        </SoftBox>
                    </Grid>}
                <Grid item xs={12} sm={dense === true ? 12 : 8}>
                    <SoftBox mb={2} textAlign="left">

                        <Grid container xs={12}>
                            <Grid item xs={4} >
                                <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize">
                                        {name.label}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={8}>
                                <FormField disabled={!canEditSchedule()}
                                    type={name.type}
                                    name={name.name}
                                    value={nameV}
                                    placeholder={name.placeholder}
                                    error={errors.name && touched.name}
                                    success={nameV && nameV.length > 0 && !errors.name}
                                />
                            </Grid>
                        </Grid>

                        <Grid container xs={12}>
                            <Grid item xs={4} >
                                <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize">
                                        {description.label}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={8}>
                                <FormField disabled={!canEditSchedule()}
                                    type={description.type}
                                    name={description.name}
                                    value={descriptionV}
                                    placeholder={description.placeholder}
                                    error={errors.description && touched.description}
                                    success={descriptionV && descriptionV.length > 0 && !errors.description}
                                />
                            </Grid>
                        </Grid>

                        <Grid container xs={12}>
                            <Grid item xs={4} >
                                <SoftBox mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize"
                                    >
                                        {rebateScheduleScheme.label}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={8}>
                                <SoftTypography
                                    variant="caption"
                                    textTransform="capitalize">
                                    {canEditSchedule() ? <>
                                        <RadioGroup row={true} defaultValue={rebateScheduleSchemeV} value={rebateScheduleSchemeV} onChange={onRebateSchedChanged} style={{ marginLeft: "1.25em", marginTop: "3px" }}>
                                            <CustomWidthTooltip title={getFeeSchemeDescription(0)} placement="bottom">
                                                <FormControlLabel value="0" control={<Radio />} label={getFeeShemeDisplayText(0)} />
                                            </CustomWidthTooltip>
                                            <CustomWidthTooltip title={getFeeSchemeDescription(1)} placement="bottom">
                                                <FormControlLabel value="1" control={<Radio />} label={getFeeShemeDisplayText(1)} />
                                            </CustomWidthTooltip>
                                            <CustomWidthTooltip title={getFeeSchemeDescription(2)} placement="bottom">
                                                <FormControlLabel value="2" control={<Radio />} label={getFeeShemeDisplayText(2)} />
                                            </CustomWidthTooltip>
                                        </RadioGroup>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            color="error">
                                            <ErrorMessage name={rebateScheduleScheme.name} />
                                        </SoftTypography>
                                    </> : getFeeShemeDisplayText(rebateScheduleSchemeV)}
                                </SoftTypography>
                            </Grid>
                        </Grid>

                        {rebateScheduleSchemeV != 0 ? <>

                            <Grid container xs={12}>
                                <Grid item xs={4} >
                                    <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize"
                                        >
                                            {rebateRate.label}
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                                <Grid item xs={8}>
                                    <FormField disabled={!canEditSchedule()}
                                        type={rebateRate.type}
                                        name={rebateRate.name}
                                        value={rebateRateV}
                                        placeholder={rebateRate.placeholder}
                                        error={errors.rebateRate && touched.rebateRate}
                                        success={rebateRateV && rebateRateV.toString().length > 0 && !errors.rebateRate}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container xs={12}>
                                <Grid item xs={4} >
                                    <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize"
                                        >
                                            {rebateThreshold.label}
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                                <Grid item xs={8}>
                                    <FormField disabled={!canEditSchedule()}
                                        type={rebateThreshold.type}
                                        name={rebateThreshold.name}
                                        value={rebateThresholdV}
                                        placeholder={rebateThreshold.placeholder}
                                        error={errors.rebateThreshold && touched.rebateThreshold}
                                        success={rebateThresholdV && rebateThresholdV.toString().length > 0 && !errors.rebateThreshold}
                                    />
                                </Grid>
                            </Grid>

                            {cardroom.type !== 6 ?
                                <Grid container xs={12}>
                                    <Grid item xs={4} >
                                        <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                            <SoftTypography
                                                component="label"
                                                variant="caption"
                                                fontWeight="bold"
                                                textTransform="capitalize"
                                            >
                                                {lossOnly.label}
                                            </SoftTypography>
                                        </SoftBox>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <FormSwitch mr="auto" readOnly={!canEditSchedule()}
                                            name={lossOnly.name}
                                            value={lossOnlyV}
                                            {...formData} />

                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            color="info" >
                                            {lossOnlyV ?
                                                "Rebate will be awarded only when player posts losses."
                                                : "Rebate will be awarded irrespective of player's results."}

                                        </SoftTypography>
                                    </Grid>
                                </Grid> :
                                <Grid container xs={12}>
                                    <Grid item xs={4} >
                                        <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                            <SoftTypography
                                                component="label"
                                                variant="caption"
                                                fontWeight="bold"
                                                textTransform="capitalize"
                                            >
                                                {makeupThreshold.label}
                                            </SoftTypography>
                                        </SoftBox>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <FormField disabled={!canEditSchedule()}
                                            type={makeupThreshold.type}
                                            name={makeupThreshold.name}
                                            value={makeupThresholdV}
                                            placeholder={makeupThreshold.placeholder}
                                            error={errors.makeupThreshold && touched.makeupThreshold}
                                            success={makeupThresholdV && makeupThresholdV.toString().length > 0 && !errors.makeupThreshold}
                                        />
                                    </Grid>
                                </Grid>}
                        </>
                            : null}

                    </SoftBox>
                </Grid>
            </Grid >
        </SoftBox >
    </SoftBox >;
};

RebateScheduleEditorForm.propTypes = {
    cardoom: PropTypes.object.isRequired,
    agency: PropTypes.object.isRequired,
    dense: PropTypes.bool
};

const RebateScheduleEditor = ({ cardroom, agency, club, book, schedule, onClose, dense }) => {

    let count = 0; if (agency) count++; if (club) count++; if (book) count++;

    if (count != 1) {
        throw new Error("Must specify either book, agency or club, all or none is not supported");
    }


    const { formId, formField } = form;
    const currentValidation = validations.editor;

    const [retryCounter, setRetryCounter] = useState(0);
    const [scheduleRecord, setScheduleRecord] = useState(null);

    function handleRetry() {
        setRetryCounter(retryCounter + 1);
    }
    const [showProgress, progressIndicatorProps] = useProgressIndicator("wait", "saving rebate schedule...", handleRetry)

    const { error, execute } = useFetchWithMsal({
        scopes: cardroom.type === 6 ? apiConfig.book.scopes.write : apiConfig.club.scopes.write,
    });

    const [context, actions, features] = useCardroomContext(cardroom);

    function canEditSchedule() {
        if (!context) return false;

        return context.isAllowed(actions.cardroom.fees.rebate.edit);
    }

    const handleSubmit = (values, actions) => {

        //alert("submitting");

        if (false === canEditSchedule()) return;

        //construct participant object
        const ns = Object.assign({}, schedule);

        //update schedule...
        if (!ns.id) ns.id = "00000000-0000-0000-0000-000000000000";

        ns.name = values.name;
        ns.description = values.description;
        ns.rate = parseFloat(values.rebateRate) / 100;
        ns.threshold = values.rebateThreshold;
        ns.scheme = values.rebateScheduleScheme;

        if (cardroom.type !== 6) {
            ns.rebateOnLossOnly = values.lossOnly;
        } else {
            ns.makeupThreshold = values.makeupThreshold;
        }

        setScheduleRecord(ns);
    };


    useEffect(() => {
        if (scheduleRecord) {

            progressIndicatorProps.setMode("wait");
            progressIndicatorProps.setMessage("Saving rebate schedule...")
            showProgress();


            let url = ""
            if (cardroom.type !== 6) {
                // [HttpPost("{agencyId:Guid}/casino/{casinoId:Guid}/schedules/rebate", Name = "SavePlayerRebateSchedule")]
                url = apiConfig.club.endpoint;
                if (agency) {
                    url += "/agency/" + agency.id + "/casino/" + cardroom.id + "/schedules/rebate";
                }
                else if (club) {
                    url += "/club/" + club.id + "/casino/" + cardroom.id + "/schedules/rebate";
                }
            }
            else {
                //[HttpPost("{bookId:Guid}/casino/{casinoId:Guid}/schedules/rebate", Name = "SavePlayerRebateSchedule")]
                url = apiConfig.book.endpoint + "/sportsbook/" + book.id + "/casino/" + cardroom.id + "/schedules/rebate";
            }

            execute("POST", url, scheduleRecord).then((response) => {
                if (response) {
                    //raiseOnTransactionCreated();
                    if (schedule) schedule = Object.assign(schedule, response);
                    else schedule = response;
                }

                setScheduleRecord(null);
                progressIndicatorProps.close();
                raiseOnClose(false);

            }).catch((e) => {
                progressIndicatorProps.setMode("errorWithRetry");
                progressIndicatorProps.setMessage("Unable to save rebate schedule, please try again...")
            });
        }

    }, [scheduleRecord, execute, retryCounter]);


    function onCanceled() {
        raiseOnClose(true);
    }

    function raiseOnClose(canceled) {
        if (onClose) {
            onClose(canceled);
        }
    }

    return <Formik
        initialValues={initialValues}
        validationSchema={currentValidation}
        onSubmit={handleSubmit}
    >
        {({ values, errors, touched, setFieldValue, setFieldError, setFieldTouched, validateForm, resetForm, isSubmitting }) => {
            return <Form id={formId} autoComplete="off">

                <SoftBox p={2} >
                    <SoftBox >
                        <ProgressIndicator {...progressIndicatorProps} />

                        {progressIndicatorProps.visible ? <></> : <>
                            <RebateScheduleEditorForm cardroom={cardroom} agency={agency} club={club} schedule={schedule} dense={dense} formData={{
                                values,
                                touched,
                                formField,
                                errors,
                                setFieldValue,
                                setFieldError,
                                setFieldTouched,
                                validateForm,
                                resetForm
                            }} />

                            <SoftBox mt={3} width="100%" display="flex" justifyContent="space-between">

                                <SoftButton variant="gradient" color="light" onClick={onCanceled}>
                                    cancel
                                </SoftButton>

                                <SoftButton
                                    variant="gradient"
                                    color="dark"
                                    disabled={isSubmitting || !canEditSchedule()}
                                    type="submit"
                                >
                                    save
                                </SoftButton>
                            </SoftBox>
                        </>

                        }

                    </SoftBox>
                </SoftBox>

            </Form>
        }}
    </Formik>;
};

RebateScheduleEditor.propTypes = {
    cardoom: PropTypes.object.isRequired,
    agency: PropTypes.object,
    club: PropTypes.object,
    dense: PropTypes.bool
};

export default RebateScheduleEditor;